import React, { useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Button,
  Divider,
  Paper,
  Box,
  Anchor
} from '@mantine/core';
import { IconBrandReddit, IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { MentionDetailModalProps } from './MentionDetailModal.types';
import { StatusBadge } from '../StatusBadge';
import { SubredditBadge } from '../SubredditBadge';
import { ConfidenceIndicator } from '../ConfidenceIndicator';
import { formatDateTimeWithOptions } from '@/infrastructure/utils/dateUtils';
import { useAuth } from '@/presentation/hooks/useAuth';
import { buildBusinessDetailRoute, buildPostReviewDetailRoute } from '@/presentation/routing/routes';
import { appendRedditContext } from '@/utils/redditUtils';

/**
 * MentionDetailModal Component
 *
 * Displays full details of a Reddit mention in a modal
 *
 * Features:
 * - Full Reddit post context (no truncation)
 * - Confidence score visualization
 * - Matched business information
 * - "View on Reddit" button
 * - Emotional tone/sentiment display
 *
 * Session 005: Created for mention detail view
 *
 * Usage:
 * ```tsx
 * <MentionDetailModal
 *   mention={selectedMention}
 *   opened={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
export const MentionDetailModal: React.FC<MentionDetailModalProps> = ({
  mention,
  opened,
  onClose
}): React.ReactElement => {
  // Get user role for admin-only features
  const { isAdmin } = useAuth();

  /**
   * Format date to readable string (UTC-aware)
   */
  const formatDate = useCallback((dateString: string): string => {
    return formatDateTimeWithOptions(dateString, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  /**
   * Get Reddit URL with context parameter for better readability (SESSION_206)
   */
  const redditUrl = useMemo((): string => {
    return appendRedditContext(mention.postPermalink);
  }, [mention.postPermalink]);

  /**
   * Get emotional tone display
   */
  const emotionalToneDisplay = useMemo((): string | null => {
    if (mention.emotionalTone) {
      const score = mention.emotionalToneScore ? ` (${(mention.emotionalToneScore * 100).toFixed(0)}%)` : '';
      return `${mention.emotionalTone}${score}`;
    }
    return null;
  }, [mention.emotionalTone, mention.emotionalToneScore]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>Mention Details</Title>}
      size="lg"
      padding="lg"
    >
      <Stack gap="md">
        {/* Post Title */}
        <Box>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
            Post Title
          </Text>
          <Title order={4} fw={500}>
            {mention.postTitle}
          </Title>
        </Box>

        {/* Metadata Row */}
        <Group>
          <Box>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Subreddit
            </Text>
            <SubredditBadge subreddit={mention.subreddit} />
          </Box>

          <Box>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Author
            </Text>
            <Text size="sm" fw={500}>
              u/{mention.authorUsername}
            </Text>
          </Box>

          <Box>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Date
            </Text>
            <Text size="sm" c="dimmed">
              {formatDate(mention.postCreatedAt)}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Content */}
        <Box>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
            Content
          </Text>
          <Paper p="md" bg="gray.0" radius="md">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {mention.content}
            </Text>
          </Paper>
          {/* View on Reddit link - SESSION_206: Moved here from Actions section */}
          {redditUrl && (
            <Anchor
              href={redditUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              mt="sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconBrandReddit size={16} />
              View on Reddit
            </Anchor>
          )}
        </Box>

        <Divider />

        {/* Business Match Information */}
        <Box>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
            Business Match
          </Text>
          <Stack gap="xs">
            {mention.businessName ? (
              <Group>
                {mention.businessId ? (
                  <Anchor
                    component={Link}
                    to={buildBusinessDetailRoute(mention.businessId)}
                    size="sm"
                    fw={500}
                  >
                    {mention.businessName}
                  </Anchor>
                ) : (
                  <Text size="sm" fw={500}>
                    {mention.businessName}
                  </Text>
                )}
                <StatusBadge status={mention.reviewStatus as any} size="sm" />
              </Group>
            ) : (
              <Text size="sm" c="dimmed" fs="italic">
                No business matched
              </Text>
            )}
          </Stack>
        </Box>

        {/* Confidence Score */}
        {mention.matchConfidenceScore !== null && mention.matchConfidenceScore > 0 && (
          <Box>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={8}>
              Match Confidence
            </Text>
            <ConfidenceIndicator
              score={mention.matchConfidenceScore}
              variant="progress"
              showPercentage
              showLabel
            />

            {/* Confidence Score Explanation - Admin only (SESSION_206) */}
            {isAdmin && (
              <Paper p="sm" bg="blue.0" radius="md" mt="sm">
                <Text size="xs" fw={600} mb={4}>
                  How this score was calculated:
                </Text>
                <Stack gap={4}>
                  <Text size="xs">
                    • Base match: {mention.matchStatus === 'Verified' ? 'Exact alias (95%)' :
                      mention.matchStatus === 'Matched' ? 'Name similarity (70-85%)' :
                      'Fuzzy match (60-70%)'}
                  </Text>
                  {mention.businessName && (
                    <Text size="xs">
                      • Matched to: {mention.businessName}
                    </Text>
                  )}
                  <Text size="xs">
                    • Regional filtering: Applied {mention.subreddit ? `(r/${mention.subreddit})` : ''}
                  </Text>
                  {mention.emotionalToneScore && mention.emotionalToneScore > 0.7 && (
                    <Text size="xs">
                      • NER boost: +3-5% (organization confirmed)
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" mt={4} fs="italic">
                    Scores below 70% are likely false positives and should be reviewed carefully.
                  </Text>
                </Stack>
              </Paper>
            )}
          </Box>
        )}

        {/* Emotional Tone */}
        {emotionalToneDisplay && (
          <Box>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
              Emotional Tone
            </Text>
            <Badge variant="light" size="lg">
              {emotionalToneDisplay}
            </Badge>
          </Box>
        )}

        {/* Reddit Metrics */}
        <Group>
          {mention.upvoteCount !== null && mention.upvoteCount > 0 && (
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Upvotes
              </Text>
              <Text size="sm" fw={500}>
                {mention.upvoteCount.toLocaleString()}
              </Text>
            </Box>
          )}

          {mention.commentCount !== null && mention.commentCount > 0 && (
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Comments
              </Text>
              <Text size="sm" fw={500}>
                {mention.commentCount.toLocaleString()}
              </Text>
            </Box>
          )}

          {mention.isComment && (
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Type
              </Text>
              <Badge variant="light" size="sm">
                Comment
              </Badge>
            </Box>
          )}
        </Group>

        <Divider />

        {/* Actions */}
        <Group justify="space-between">
          {/* Admin-only: View Post Review link */}
          {isAdmin && mention.redditPostId ? (
            <Button
              component={Link}
              to={buildPostReviewDetailRoute(mention.redditPostId)}
              variant="light"
              leftSection={<IconExternalLink size={16} />}
            >
              View Post Review
            </Button>
          ) : (
            <div /> /* Spacer for layout when no admin button */
          )}

          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
